import { adminFetch } from './dataProvider';

/**
 * CRM capability check for the Batch screen. Like crmConnections.ts this uses
 * `adminFetch` directly rather than the Refine data provider because the
 * backend route (`/v1/admin/crm/capabilities`) doesn't fit the resource-name
 * convention `/v1/admin/{resource}`.
 *
 * The own-company Salesforce EXPORT calls that used to live here
 * (`exportToSalesforce`, `fetchSalesforceMembers`) were retired in p3 Task 6
 * — superseded by the CRM merge queue (`./crmUpload.ts`'s `CrmQueueApi`
 * fetchers: match-check/preview/commit/owners).
 */

/** One provider's slice of the capabilities body. */
export interface CrmSourceCapability {
  /**
   * Per-company entitlement for THIS provider (`companies.crm_<source>_enabled`,
   * migration 044). Optional only because a backend deployed before per-CRM
   * flags omits it; absent reads as not enabled.
   */
  enabled?: boolean;
  /** A stored credential exists AND the provider is enabled. */
  connected: boolean;
}

export interface CrmCapabilities {
  salesforce?: CrmSourceCapability;
  hubspot?: CrmSourceCapability;
  zoho?: CrmSourceCapability;
  /**
   * Deploy-order bridge: "any provider enabled" plus "rows exist". Nothing in
   * this portal reads it any more — it stays on the wire until the backend's
   * Backlog cleanup removes it.
   */
  integrations?: { enabled: boolean; hasConnections?: boolean };
}

/**
 * Own-company CRM capability flags, one per configured source (the backend
 * reports `salesforce`/`hubspot`/`zoho` — see p2's `capabilitiesCore`), each
 * carrying its own per-provider `enabled`/`connected` pair.
 *
 * **Rejects on failure** (network, 401, 403, 5xx, malformed body). It used to
 * swallow every error and resolve to `{}`, which react-query cannot tell from
 * a real answer: the query recorded a SUCCESS — so retry/backoff never
 * engaged — and cached the empty body for the whole `staleTime`. One
 * transient 500 therefore read as an authoritative "nothing is enabled" for
 * five minutes.
 *
 * Failing closed still belongs in the app — it just belongs where the two
 * cases can be told apart. The Integrations nav entry and page are shown
 * unconditionally now; a failed probe that never held data makes `CrmGuard`
 * show a Retry instead of rendering three greyed cards that claim nothing is
 * granted. Within the page, each provider's `enabled` bit drives whether its
 * card is live or greyed, and `connected` drives Clean / Match / Send on the
 * Batch screen.
 */
export async function fetchCrmCapabilities(): Promise<CrmCapabilities> {
  return adminFetch<CrmCapabilities>('/v1/admin/crm/capabilities');
}
